import {BrowserRouter, Navigate, Route, Routes, useLocation} from 'react-router-dom';
import {User} from 'firebase/auth';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {useAuth} from './hooks/useAuth';
import {LoginPage} from './components/LoginPage';
import {Layout} from './components/Layout';
import {DashboardPage} from './pages/DashboardPage';
import {FeedbackDetailPage} from './pages/FeedbackDetailPage';
import {TeamMembersPage} from './pages/TeamMembersPage';
import {AppVersionsPage} from './pages/AppVersionsPage';
import {BugSynthesisPage} from './pages/BugSynthesisPage';
import {BugsPage} from './pages/BugsPage';
import {ADMIN_ROOT, LEGACY_ADMIN_PATHS, PUBLIC_FORM_URL} from './routes';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

/**
 * Le formulaire public ouvre une session ANONYME pour satisfaire les regles
 * Firestore. Sans cette distinction, un membre de l'equipe qui a d'abord
 * consulte le formulaire se verrait refuser l'entree du back-office par un
 * « Access Denied » au lieu de l'ecran de connexion : il est bien connecte,
 * mais sous une identite qui n'est celle de personne.
 */
function isSignedInPerson(user: User | null): boolean {
  return user != null && !user.isAnonymous;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, loading, error } = useAuth();

  if (loading) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#f0f0f0]">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isSignedInPerson(user)) {
    return <LoginPage />;
  }

  if (!isAdmin) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#f0f0f0]">
        <div className="max-w-md w-full bg-white rounded-lg shadow p-8 text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            {error || 'You do not have permission to access this admin panel.'}
          </p>
          <p className="text-sm text-gray-500">
            Signed in as: {user?.email}
          </p>
        </div>
      </div>
    );
  }

  return <Layout>{children}</Layout>;
}

/**
 * La racine de feedback.assimil.app est l'adresse que nous donnons dans nos
 * reponses aux avis des boutiques : elle mene au formulaire, qui vit
 * desormais sur le site. Le back-office a sa propre porte, /admin.
 *
 * La redirection est deja posee dans index.html, avant meme le chargement du
 * bundle ; celle-ci rattrape le cas ou l'on arrive ici par navigation interne.
 */
function HomeRoute() {
  window.location.replace(PUBLIC_FORM_URL + window.location.search);
  return null;
}

/**
 * Les adresses d'avant le passage sous /admin. L'equipe a des favoris et les
 * rapports deja envoyes portent d'anciens liens : on les suit plutot que de
 * les casser.
 */
function LegacyAdminPath() {
  const location = useLocation();
  return <Navigate to={`${ADMIN_ROOT}${location.pathname}${location.search}`} replace />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomeRoute />} />

          {/* Back-office */}
          <Route
            path={ADMIN_ROOT}
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ADMIN_ROOT}/feedback/:id`}
            element={
              <ProtectedRoute>
                <FeedbackDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ADMIN_ROOT}/team-members`}
            element={
              <ProtectedRoute>
                <TeamMembersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ADMIN_ROOT}/app-versions`}
            element={
              <ProtectedRoute>
                <AppVersionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ADMIN_ROOT}/bugs`}
            element={
              <ProtectedRoute>
                <BugsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={`${ADMIN_ROOT}/bug-synthesis`}
            element={
              <ProtectedRoute>
                <BugSynthesisPage />
              </ProtectedRoute>
            }
          />

          {/* Anciennes adresses du back-office, suivies vers /admin */}
          {LEGACY_ADMIN_PATHS.map((path) => (
            <Route key={path} path={path} element={<LegacyAdminPath />} />
          ))}
          <Route path="/login" element={<Navigate to={ADMIN_ROOT} replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
